/**
 * Evidence Panel Component
 * Main evidence display for issue detail panel with stage gates and timeline
 */

import { useState, useEffect } from 'react';
import { RefreshCw, AlertTriangle, CheckCircle2, XCircle } from 'lucide-react';
import { evidenceApi, type Evidence, type LifecycleResponse, type EvidenceSummary } from '../../../api/evidence';
import { StageGateStatus, StageGateProgress } from './StageGateStatus';
import { EvidenceTimeline } from './EvidenceTimeline';

interface EvidencePanelProps {
  issueId: string;
  worktreeId?: string;
  boardId?: string;
  compact?: boolean;
}

export function EvidencePanel({ issueId, compact = false }: EvidencePanelProps) {
  const [evidence, setEvidence] = useState<Evidence[]>([]);
  const [summary, setSummary] = useState<EvidenceSummary | null>(null);
  const [lifecycle, setLifecycle] = useState<LifecycleResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'timeline'>('overview');

  const loadEvidence = async () => {
    setLoading(true);
    setError(null);

    try {
      const [evidenceRes, lifecycleRes] = await Promise.all([
        evidenceApi.getEvidence(issueId),
        evidenceApi.getLifecycle(issueId),
      ]);

      setEvidence(evidenceRes.evidence);
      setSummary(evidenceRes.summary);
      setLifecycle(lifecycleRes);
    } catch (err) {
      console.error('Failed to load evidence:', err);
      setError(err instanceof Error ? err.message : 'Failed to load evidence');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEvidence();
  }, [issueId]);

  if (loading) {
    return (
      <div
        className="bg-white rounded-lg p-6 shadow-warm-md animate-pulse"
        style={{ borderColor: '#E8E0D5', borderWidth: '1px', borderStyle: 'solid' }}
      >
        <div className="h-6 w-32 rounded mb-4" style={{ backgroundColor: '#E8E0D5' }} />
        <div className="space-y-3">
          <div className="h-16 rounded" style={{ backgroundColor: '#F5F1EC' }} />
          <div className="h-16 rounded" style={{ backgroundColor: '#F5F1EC' }} />
          <div className="h-16 rounded" style={{ backgroundColor: '#F5F1EC' }} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="bg-white rounded-lg p-6 shadow-warm-md"
        style={{ borderColor: '#E8E0D5', borderWidth: '1px', borderStyle: 'solid' }}
      >
        <div className="flex items-center gap-3 text-red-500 mb-4">
          <AlertTriangle className="w-5 h-5" />
          <span className="font-medium">Failed to load evidence</span>
        </div>
        <p className="text-sm mb-4" style={{ color: '#6B5D52' }}>
          {error}
        </p>
        <button
          onClick={loadEvidence}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium"
          style={{ backgroundColor: '#F5F1EC', color: '#6B5D52' }}
        >
          <RefreshCw className="w-4 h-4" />
          Retry
        </button>
      </div>
    );
  }

  // Compact view for sidebar or list
  if (compact) {
    return (
      <div className="space-y-3">
        {lifecycle && (
          <StageGateProgress lifecycle={lifecycle.lifecycle} currentStage={lifecycle.currentStage} />
        )}
        {summary && (
          <div className="flex items-center gap-4 text-sm" style={{ color: '#6B5D52' }}>
            <span className="flex items-center gap-1">
              <CheckCircle2 className="w-4 h-4" style={{ color: '#22C55E' }} />
              {summary.validCount}
            </span>
            {summary.invalidCount > 0 && (
              <span className="flex items-center gap-1">
                <XCircle className="w-4 h-4" style={{ color: '#EF4444' }} />
                {summary.invalidCount}
              </span>
            )}
            <span>Avg: {Math.round(summary.avgQualityScore)}%</span>
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className="bg-white rounded-lg shadow-warm-md"
      style={{ borderColor: '#E8E0D5', borderWidth: '1px', borderStyle: 'solid' }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: '#E8E0D5' }}>
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-semibold" style={{ color: '#2F241B' }}>
            Evidence & Stage Gates
          </h3>
          {summary && (
            <span
              className="text-sm px-2 py-0.5 rounded-full"
              style={{ backgroundColor: '#F5F1EC', color: '#6B5D52' }}
            >
              {summary.totalEvidence} items
            </span>
          )}
        </div>
        <button
          onClick={loadEvidence}
          className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          title="Refresh evidence"
        >
          <RefreshCw className="w-4 h-4" style={{ color: '#6B5D52' }} />
        </button>
      </div>

      {/* Stage Progress Bar */}
      {lifecycle && (
        <div className="px-6 py-4 border-b" style={{ borderColor: '#E8E0D5', backgroundColor: '#FAFAFA' }}>
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium" style={{ color: '#6B5D52' }}>
              Current Stage: <span style={{ color: '#D97F6F' }}>{lifecycle.currentStage}</span>
            </span>
            {summary && (
              <span className="text-sm" style={{ color: '#A39686' }}>
                Quality Score: {Math.round(summary.avgQualityScore)}%
              </span>
            )}
          </div>
          <StageGateProgress lifecycle={lifecycle.lifecycle} currentStage={lifecycle.currentStage} />
        </div>
      )}

      {/* Summary Stats */}
      {summary && summary.totalEvidence > 0 && (
        <div
          className="grid grid-cols-4 gap-4 px-6 py-4 border-b"
          style={{ borderColor: '#E8E0D5' }}
        >
          <div className="text-center">
            <p className="text-2xl font-semibold" style={{ color: '#2F241B' }}>
              {summary.totalEvidence}
            </p>
            <p className="text-xs" style={{ color: '#A39686' }}>
              Total
            </p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-semibold" style={{ color: '#22C55E' }}>
              {summary.validCount}
            </p>
            <p className="text-xs" style={{ color: '#A39686' }}>
              Valid
            </p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-semibold" style={{ color: '#EF4444' }}>
              {summary.invalidCount}
            </p>
            <p className="text-xs" style={{ color: '#A39686' }}>
              Invalid
            </p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-semibold" style={{ color: '#F59E0B' }}>
              {summary.pendingCount}
            </p>
            <p className="text-xs" style={{ color: '#A39686' }}>
              Pending
            </p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex border-b" style={{ borderColor: '#E8E0D5' }}>
        <button
          onClick={() => setActiveTab('overview')}
          className={`px-6 py-3 text-sm font-medium transition-colors ${
            activeTab === 'overview' ? 'border-b-2' : ''
          }`}
          style={{
            color: activeTab === 'overview' ? '#D97F6F' : '#6B5D52',
            borderColor: activeTab === 'overview' ? '#D97F6F' : 'transparent',
          }}
        >
          Stage Overview
        </button>
        <button
          onClick={() => setActiveTab('timeline')}
          className={`px-6 py-3 text-sm font-medium transition-colors ${
            activeTab === 'timeline' ? 'border-b-2' : ''
          }`}
          style={{
            color: activeTab === 'timeline' ? '#D97F6F' : '#6B5D52',
            borderColor: activeTab === 'timeline' ? '#D97F6F' : 'transparent',
          }}
        >
          Timeline
        </button>
      </div>

      {/* Content */}
      <div className="p-6">
        {activeTab === 'overview' && lifecycle && (
          <div className="space-y-4">
            {(['DEVELOPMENT', 'TESTING', 'REVIEW', 'MERGE', 'COMPLETION'] as const).map((stage) => (
              <StageGateStatus
                key={stage}
                stage={stage}
                lifecycle={lifecycle.lifecycle[stage]}
                gateStatus={lifecycle.lifecycle[stage]?.gateStatus}
              />
            ))}
          </div>
        )}

        {activeTab === 'timeline' && <EvidenceTimeline evidence={evidence} groupByStage={true} />}
      </div>
    </div>
  );
}

export default EvidencePanel;
